<script setup lang="ts">
import { titleBar } from '@/composables/menu/title'
import { useI18n } from 'vue-i18n'
import { computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import OpenItem from '@/components/open/OpenItem.vue'
import ResponsivePagination from '@/components/base/common/ResponsivePagination.vue'
import { CODE_OK } from '@/apis/code'
import { getOpenMemorial, type GetOpenMemorialResponse } from '@/composables/memorize/memorize'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const requestURL = useRequestURL()

const pageSize = 50
const familyName = route.params.familyName as string

onMounted(() => {
  titleBar.value.hideBack = false
  titleBar.value.backable = true
  titleBar.value.transparent = false
  titleBar.value.title = `${familyName}姓氏纪念馆列表`
})

const nuxtApp = useNuxtApp() as any
if (!nuxtApp.payload.data) {
  nuxtApp.payload.data = {}
}
if (nuxtApp.static && !nuxtApp.static.data) {
  nuxtApp.static.data = {}
}

const currentPage = computed({
  get: () => Number(route.query.page) || 1,
  set: (val) => {
    router.replace({ query: { ...route.query, page: String(val) } })
  }
})

const canonicalUrl = computed(() => {
  const url = new URL(`${requestURL.origin}${route.path}`)
  if (route.query.page != null) {
    url.searchParams.set('page', String(route.query.page))
  }
  return url.toString()
})

const pageTitle = computed(() => `${familyName}姓氏纪念馆列表`.trim())
const pageDescription = computed(() => `${familyName}姓氏下的纪念馆列表`.trim())

useSeoMeta({
  title: pageTitle,
  ogTitle: pageTitle,
  description: pageDescription,
  ogDescription: pageDescription,
  ogUrl: canonicalUrl,
  twitterCard: 'summary_large_image'
})

useHead({
  link: [
    {
      rel: 'canonical',
      href: canonicalUrl
    }
  ]
})

const openFamilyMemorialData = useState<GetOpenMemorialResponse | null>(
  `open-family-memorial-${familyName}`,
  () => null
)

const { data: ssrData } = await useAsyncData(
  `open-family-memorial-data-${familyName}`,
  async () => {
    const { data } = await getOpenMemorial(familyName, currentPage.value, pageSize)
    return data.value
  },
  { watch: [currentPage] }
)

watch(
  () => ssrData.value,
  (value) => {
    if (value && value.code === CODE_OK) {
      openFamilyMemorialData.value = (value.data as GetOpenMemorialResponse) ?? null
    }
  },
  { immediate: true }
)

const currentList = computed(() => {
  return (
    openFamilyMemorialData.value?.lives.filter((life) => {
      return life.memorialServices !== null
    }) || []
  )
})
const totalItems = computed(() => openFamilyMemorialData.value?.total ?? 0)
const totalPages = computed(() => Math.max(1, Math.ceil(totalItems.value / pageSize)))

function mapLifeToItem(life: LifeDTO) {
  const birthYear = life.birthday?.date != null ? new Date(life.birthday.date).getFullYear() : ''
  const deathYear = life.dayDied?.date != null ? new Date(life.dayDied.date).getFullYear() : ''

  return {
    id: life.memorialServices[0].id,
    name: life.name?.fullName ?? '',
    avatar: life.avatar?.middleQuality || life.avatar?.url || '',
    birthYear,
    deathYear
  }
}

const onPageChange = () => {
  if (import.meta.client) {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }
}
</script>

<template>
  <BaseLayout min-h-screen>
    <div class="w-full bg-white pt-14 md:pt-4 pb-12 px-4 flex flex-col">
      <div class="text-[26px] font-bold mb-6 text-sblack">{{ familyName }}姓氏纪念馆列表</div>
      <div class="flex-1 space-y-3" v-if="currentList.length > 0">
        <OpenItem
          v-for="item in currentList"
          :key="item.id"
          :item="mapLifeToItem(item)"
          nameSuffix=""
          public
          :desc="item.figureSummary" />
      </div>
      <div v-else text-base text-third mx-auto my-20>
        {{ t('message.no_data') }}
      </div>
      <div class="pb-4 mt-8 w-full flex justify-cetner items-center flex-col" v-if="totalPages > 1">
        <ResponsivePagination
          v-model="currentPage"
          :total="totalPages"
          mode="numeric"
          @change="onPageChange"
          public
          seoMode="seo" />
        <div class="text-[14px] text-gray-800 mt-6 font-medium text-center md:text-left">共{{ totalItems }}条</div>
      </div>
    </div>
  </BaseLayout>
</template>
